import { useState } from 'react'
import { API_USER_CHAT_IMAGES } from '../../../../constants/endpoints'
import { AppStateType, useAppStateSelector } from '../../../Hooks/useAppState'
import { Status } from '../../../common/StatusSnackbar'
import {
    FileInputContainer,
    FileInputLabel,
    FileNameDisplay,
    HeaderText,
    HiddenInput,
    LabelText,
    Row,
    Section,
    StyledButton,
    StyledTextField,
    isValidUrl,
} from './Shared'

interface ChatImageInsertionProps {
    onInsertImage: (shortcut: string) => void
    setStatus: (status: Status) => void
}

const ChatImageInsertion = (props: ChatImageInsertionProps) => {
    const { onInsertImage, setStatus } = props
    const [fileName, setFileName] = useState('No file chosen')
    const [selectedFile, setSelectedFile] = useState(null)
    const [imageUrl, setImageUrl] = useState('')
    const [loading, setLoading] = useState(false)
    const isMobile = useAppStateSelector(AppStateType.APP, 'isMobile')
    const token = useAppStateSelector(AppStateType.SESSION, 'token')

    const handleFileChange = (e) => {
        if (e.target.files.length > 0) {
            setFileName(e.target.files[0].name)
            setSelectedFile(e.target.files[0])
        } else {
            setFileName('No file chosen')
            setSelectedFile(null)
        }
    }

    const showStatus = (message: string, error = false) => {
        setStatus({ error, message })
    }

    const handleInsertImageUrl = () => {
        if (!imageUrl.trim()) {
            showStatus('Please enter an image URL', true)
            return
        }

        if (!isValidUrl(imageUrl)) {
            showStatus('Please enter a valid URL', true)
            return
        }

        setImageUrl('')
        onInsertImage(imageUrl + ' ')
    }

    const handleInsertImageFile = async () => {
        if (!selectedFile) {
            showStatus('Please select an image file first', true)
            return
        }

        setLoading(true)

        try {
            const formData = new FormData()
            formData.append('inline_chat_image_file', selectedFile)
            formData.append('token', token)

            const response = await fetch(
                `${API_USER_CHAT_IMAGES}/client/inlineChatImageFile`,
                {
                    method: 'POST',
                    body: formData,
                    credentials: 'include',
                },
            )

            const resp = await response.json()

            if (resp.err || resp.errmsg?.message) {
                const errorMessage =
                    resp.errmsg?.message ||
                    'Please contact support. Error #M98724'
                throw new Error(errorMessage)
            }

            if (
                resp.result?.success === 1 &&
                typeof resp.result.shortcut === 'string'
            ) {
                setFileName('No file chosen')
                setSelectedFile(null)
                onInsertImage(':' + resp.result.shortcut)
            } else {
                throw new Error('Please contact support. Error #M98724')
            }
        } catch (error) {
            console.error('Error uploading image:', error)
            showStatus(`Upload failed: ${error.message}`, true)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Section>
            <HeaderText>Insert an image into chat</HeaderText>
            <LabelText>
                Please do not post anything inappropriate, pornographic,
                illegal, or offensive.
            </LabelText>

            <Row>
                <StyledTextField
                    placeholder="https://..."
                    variant="outlined"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                />
                <StyledButton
                    disableElevation
                    onClick={handleInsertImageUrl}
                    disabled={!imageUrl.trim()}
                >
                    {!isMobile ? 'INSERT IMAGE URL' : 'INSERT URL'}
                </StyledButton>
            </Row>

            <Row>
                <FileInputContainer>
                    <FileInputLabel htmlFor="image-file">
                        CHOOSE FILE
                    </FileInputLabel>
                    <HiddenInput
                        type="file"
                        id="image-file"
                        accept="image/*"
                        onChange={handleFileChange}
                    />
                </FileInputContainer>
                <div style={{ flexGrow: 1 }} />
                <StyledButton
                    disableElevation
                    onClick={handleInsertImageFile}
                    disabled={loading || !selectedFile}
                >
                    {loading
                        ? 'UPLOADING...'
                        : !isMobile
                          ? 'INSERT IMAGE FILE'
                          : 'INSERT FILE'}
                </StyledButton>
            </Row>

            <FileNameDisplay>{fileName}</FileNameDisplay>
        </Section>
    )
}

export default ChatImageInsertion
